import echarts from './echarts';

/*
 * This originated from an open source project on Github
 * URL: https://github.com/kelvinau/circular-audio-wave/blob/master/src/js/circular-audio-wave.js
 */

/* global alert, XMLHttpRequest, AudioContext, OfflineAudioContext, requestAnimationFrame */

export default class CircularAudioWave {
  constructor(elem, onEndCallback, opts = {}) {
    this.opts = opts;
    this.lastMaxR = 0;
    this.maxChartValue = 1240;
    this.minChartValue = 900;
    this.chart = echarts.init(elem);
    this.playing = false;
    this.tick = 0;
    this.onEndCallback = onEndCallback;

    this.defaultChartOption = {
      angleAxis: {
        type: 'value',
        clockwise: false,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      radiusAxis: {
        min: 0,
        max: this.maxChartValue + 50,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      polar: {
        radius: '100%',
      },
      series: [
        {
          coordinateSystem: 'polar',
          name: 'line',
          type: 'line',
          showSymbol: false,
          lineStyle: {
            color: {
              colorStops: [
                {
                  offset: 0.7,
                  color: '#F16153',
                },
                {
                  offset: 0.3,
                  color: '#4EB3D4',
                },
              ],
            },
            shadowColor: 'blue',
            shadowBlur: 10,
          },
          zlevel: 2,
          data: Array.apply(null, {
            length: 361,
          }).map(Function.call, i => {
            return [this.minChartValue, i];
          }),
          silent: true,
          hoverAnimation: false,
        },
      ],
    };
    // check if the default naming is enabled, if not use the chrome one.
    window.AudioContext = window.AudioContext || window.webkitAudioContext;
    window.OfflineAudioContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;

    if (!window.AudioContext) {
      alert('Your browser does not support Web Audio API');
    } else {
      this.context = new AudioContext();
      this.offlineContext = new OfflineAudioContext(2, 30 * 44100, 44100);
      this.sourceNode = this.context.createBufferSource();
      this.offlineSource = this.offlineContext.createBufferSource();
      this.sourceNode.loop = !!this.opts.loop;
      this.analyser = this.context.createAnalyser();
    }

    this.chartOption = JSON.parse(JSON.stringify(this.defaultChartOption));
  }

  loadAudio(filePath) {
    this._setupAudioNodes();
    this._setupOfflineContext();
    const request = new XMLHttpRequest();
    request.open('GET', filePath, true);
    request.responseType = 'arraybuffer';
    request.send();
    return new Promise(resolve => {
      request.onload = () => {
        // Preprocess buffer for bpm
        this.offlineContext.decodeAudioData(request.response, buffer => {
          this.sourceNode.buffer = buffer;
          this.offlineSource.buffer = buffer;
          this.offlineSource.start(0);
          this.offlineContext.startRendering();
        });

        this.offlineContext.oncomplete = e => {
          const buffer = e.renderedBuffer;
          this.bpm = this._getBPM([buffer.getChannelData(0), buffer.getChannelData(1)]);

          this._init();

          setTimeout(() => {
            this.play();
          }, 500);
          resolve();
        };
      };
    });
  }

  _init() {
    this.chart.setOption(this.chartOption, true);
    this._debouncedDraw = this._debounce(this._drawAnimation.bind(this), 25);
  }

  presetOption() {
    if (this.opts.mode !== 'sunburst') {
      this.chartOption.series[0].animation = false;
    }
  }

  play() {
    if (this && this.sourceNode && this.sourceNode.buffer) {
      this.playing = true;
      this.presetOption();
      this.sourceNode.start(0);
      this._debouncedDraw();
    }
  }

  stop() {
    if (this && this.sourceNode && this.sourceNode.buffer) {
      this.playing = false;
      this.sourceNode.stop(0);
    }
  }

  destroy() {
    this.chart.dispose();
  }

  reset() {
    this.tick = 0;
    this.chartOption = JSON.parse(JSON.stringify(this.defaultChartOption));
    this._init();
  }

  onended() {
    if (!this.opts.loop) {
      this.playing = false;
      this.context.close();
      this.sourceNode.buffer = null;
      this.offlineSource.buffer = null;
      this.reset();

      this.context = new AudioContext();
      this.offlineContext = new OfflineAudioContext(2, 30 * 44100, 44100);
      this.sourceNode = this.context.createBufferSource();
      this.offlineSource = this.offlineContext.createBufferSource();
      this.analyser = this.context.createAnalyser();

      this.onEndCallback();
    }
  }

  _setupAudioNodes() {
    this.analyser.smoothingTimeConstant = 0.3;
    this.analyser.fftSize = 2048;

    this.sourceNode.connect(this.analyser);

    this.sourceNode.connect(this.context.destination);
    this.sourceNode.onended = this.onended.bind(this);
  }

  _setupOfflineContext() {
    // Beats generally occur around the 100 to 150 hz range.
    const lowpass = this.offlineContext.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(150, 0);
    lowpass.Q.setValueAtTime(1, 0);

    this.offlineSource.connect(lowpass);

    const highpass = this.offlineContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.setValueAtTime(100, 0);
    highpass.Q.setValueAtTime(1, 0);
    lowpass.connect(highpass);
    highpass.connect(this.offlineContext.destination);
  }

  _drawAnimation() {
    const freqData = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(freqData);
    this._draw(freqData);
    requestAnimationFrame(this._debouncedDraw.bind(this));
  }

  _draw(freqData) {
    if (this.playing) {
      const waveData = this._generateWaveData(freqData);
      this.chartOption.series[0].data = waveData.data;

      if (waveData.maxR > this.lastMaxR) {
        this.lastMaxR = waveData.maxR + 4;
      } else if (this.playing) {
        this.lastMaxR -= 2;
      } else {
        this.lastMaxR = this.minChartValue;
      }

      this.chart.setOption(this.chartOption, true);
      this.tick++;
    }
  }

  _generateWaveData(data) {
    let waveData = [];
    let maxR = 0;
    if (this.opts.mode !== 'sunburst') {
      for (let i = 0; i <= 360; i++) {
        // (((OldValue - OldMin) * (NewMax - NewMin)) / (OldMax - OldMin)) + NewMin
        const freq = data[i];
        const r = ((freq - 0) * (this.maxChartValue - this.minChartValue)) / 255 + this.minChartValue;
        if (r > maxR) {
          maxR = r;
        }
        waveData.push([r, i]);
      }
      waveData.push([waveData[0][0], 360]);
    } else {
      waveData = JSON.parse(JSON.stringify(this.chartOption.series[0].data));
      let index = 0;
      waveData.forEach(level0 => {
        level0.children.forEach(level1 => {
          level1.children.forEach(item => {
            const freq = data[index];
            const r = ((freq - 0) * 40) / 255;

            item.children[0].name = Array.apply(null, {
              length: r,
            })
              .map(Function.call, () => {
                return '';
              })
              .join(' ');
            index++;
          });
        });
      });
    }
    return {
      maxR: maxR,
      data: waveData,
    };
  }

  _getBPM(data) {
    const partSize = 22050;
    const parts = data[0].length / partSize;
    let peaks = [];

    for (let i = 0; i < parts; i++) {
      let max = 0;
      for (let j = i * partSize; j < (i + 1) * partSize; j++) {
        const volume = Math.max(Math.abs(data[0][j]), Math.abs(data[1][j]));
        if (!max || volume > max.volume) {
          max = {
            position: j,
            volume: volume,
          };
        }
      }
      peaks.push(max);
    }

    peaks.sort((a, b) => {
      return b.volume - a.volume;
    });
    peaks = peaks.splice(0, peaks.length * 0.5);
    peaks.sort((a, b) => {
      return a.position - b.position;
    });

    const groups = [];
    peaks.forEach((peak, index) => {
      for (let i = 1; index + i < peaks.length && i < 10; i++) {
        const group = {
          bpm: (60 * 44100) / (peaks[index + i].position - peak.position),
          count: 1,
        };

        while (group.bpm < 90) {
          group.bpm *= 2;
        }

        while (group.bpm > 180) {
          group.bpm /= 2;
        }

        group.bpm = Math.round(group.bpm);

        if (
          !groups.some(interval => {
            return interval.bpm === group.bpm ? interval.count++ : 0;
          })
        ) {
          groups.push(group);
        }
      }
    });

    return groups.sort((intA, intB) => {
      return intB.count - intA.count;
    })[0].bpm;
  }

  _debounce(func, wait, immediate) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      const later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }
}
